import React from 'react';
import { withTranslation, TFunction } from 'next-i18next';
import ReactPlayer from 'react-player/lazy';
import { WithTranslation } from 'react-i18next';
import { Attachment } from 'schema/Post/types';
import { isLoomUrl } from 'utils/urlParser';
import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import { Button, Text, TextField } from '@thinkific/chalk';
import { atomic } from '@thinkific/chalk/atomic';
import { X } from '@thinkific/chalk/react-icons';
import VideoPreview from './VideoPreview';
import styles from './AttachmentVideoInput.module.scss';

interface AttachmentVideoInputProps extends WithTranslation {
  attachmentUrl?: string | null;
  t: TFunction;
  onChange: (attachment?: Attachment) => void;
  onClose: () => void;
}

const videoBox = atomic({
  position: 'relative',
  backgroundColor: 'action.neutral.lightest',
  marginTop: 'space.200',
  paddingTop: 'space.100',
  paddingBottom: 'space.100',
  paddingLeft: 'space.150',
  paddingRight: 'space.150',
  borderRadius: 'sm',
  borderStyle: 'solid',
  borderWidth: 'regular',
  borderColor: 'action.neutral.light',
});

const videoInputContainer = atomic({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
});

const addButton = atomic({
  alignSelf: 'end',
});

const closeButton = atomic({
  position: 'absolute',
  borderRadius: 'full',
  marginRight: 'space.100',
  marginTop: 'space.100',
  right: 0,
  top: 0,
});

const AttachmentVideoInput = ({
  attachmentUrl,
  t,
  onChange,
  onClose,
}: AttachmentVideoInputProps) => {
  const [videoUrl, setVideoUrl] = React.useState(attachmentUrl || '');
  const [isVideoUploaded, setIsVideoUploaded] = React.useState(!!attachmentUrl);
  const [errorMessage, setErrorMessage] = React.useState('');

  const handleValidation = () => {
    let urlIsValid = true;
    let newErrorMessage = '';

    const isPlayableUrl = ReactPlayer.canPlay(videoUrl) || isLoomUrl(videoUrl);

    if (!isPlayableUrl) {
      urlIsValid = false;
      newErrorMessage = t('Invalid URL');
    }
    setErrorMessage(newErrorMessage);
    return urlIsValid;
  };

  const handleUploadVideo = () => {
    if (handleValidation()) {
      setIsVideoUploaded(true);

      onChange({
        fileName: '',
        fileSizeInBytes: null,
        filestackId: '',
        s3Key: '',
        s3Bucket: '',
        url: videoUrl,
        attachmentUrl: videoUrl,
        attachmentType: PostAttachmentType.Video,
      });
    }
  };

  const handleRemoveVideo = () => {
    setIsVideoUploaded(false);
    setVideoUrl('');
    setErrorMessage('');
    onClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVideoUrl(event.target.value);
  };

  const disableAddVideoButton = () => {
    return videoUrl.length === 0 || !videoUrl;
  };

  if (isVideoUploaded) {
    return <VideoPreview videoUrl={videoUrl} onRemove={handleRemoveVideo} />;
  }

  return (
    <div className={videoBox}>
      <div className={videoInputContainer}>
        <TextField
          className={styles.inputField}
          dataQa="attachment-video-input__input"
          label={t(
            'components-attachmentVideoInput-paste_video_url',
            'Paste video url'
          )}
          placeholder={t(
            'components-attachmentVideoInput-supported_video_platforms_tip',
            'YouTube, Vimeo, Loom'
          )}
          type="url"
          required
          onChange={handleChange}
        />
        <Button
          className={addButton}
          disabled={disableAddVideoButton()}
          onClick={handleUploadVideo}
        >
          {t('Add', 'Add')}
        </Button>
      </div>
      <Button
        className={closeButton}
        dataQa="attachment-video-input__close-button"
        icon={X}
        size="xs"
        variant="default"
        hiddenLabel
        onClick={onClose}
        onDark
      >
        {t('Close', 'Close')}
      </Button>
      {errorMessage && (
        <div role="alert">
          <Text color="action.destructive.dark" variant="help-text.xs">
            {errorMessage}
          </Text>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(AttachmentVideoInput);
